import { useApiQuery } from '../useApiQuery';
import { creditMemoKeys } from '@/lib/queryKeys';
import { creditMemoEndpoints } from '@/lib/api/endpoints';
import { apiGet, operationsClient } from '@/lib/api/client';
import type { CreditMemo, CreditMemoListFilters, PaginatedCreditMemos } from '@/types/creditMemo';

function normalizeCreditMemo(raw: Record<string, unknown>): CreditMemo {
  return {
    ...(raw as object),
    id: String(raw.id ?? ''),
    creditMemoNumber: String(raw.creditMemoNumber ?? ''),
    type: String(raw.type ?? 'REFUND') as CreditMemo['type'],
    status: String(raw.status ?? 'OPEN'),
    contactId: raw.contactId ? String(raw.contactId) : null,
    invoiceId: raw.invoiceId ? String(raw.invoiceId) : null,
    lineItems: Array.isArray(raw.lineItems) ? (raw.lineItems as CreditMemo['lineItems']) : [],
    subtotal: Number(raw.subtotal ?? 0),
    taxTotal: Number(raw.taxTotal ?? 0),
    total: Number(raw.total ?? 0),
    remainingCredit: Number(raw.remainingCredit ?? 0),
    txnDate: raw.txnDate ? String(raw.txnDate) : null,
    createdAt: String(raw.createdAt ?? new Date().toISOString()),
    updatedAt: String(raw.updatedAt ?? new Date().toISOString()),
  } as CreditMemo;
}

export { normalizeCreditMemo };

export function useCreditMemos(filters: CreditMemoListFilters = {}) {
  return useApiQuery<PaginatedCreditMemos>({
    queryKey: creditMemoKeys.list(filters as Record<string, unknown>),
    queryFn: async () => {
      const raw = await apiGet<{
        data: Record<string, unknown>[];
        total: number;
        page: number;
        limit: number;
        totalPages: number;
      }>(operationsClient, creditMemoEndpoints.list(), { params: filters });
      return {
        data: raw.data.map(normalizeCreditMemo),
        total: raw.total,
        page: raw.page,
        limit: raw.limit,
        totalPages: raw.totalPages,
      };
    },
    showErrorToast: false,
  });
}
